import moment from 'moment';
import serializeTrip from './trip';

/**
 * Receives a passengers object with the type as the key and the quantity
 * as the value and returns the total number of passengers
 * ```
 * { adult: 2, child: 1, infant: 1 } => 4
 * ```
 * @method passengerCount
 * @param {Object} passengers
 * @return {Number}
 */
function passengerCount(passengers) {
  return Object.keys(passengers)
    .map((passengerType) => parseInt(passengers[passengerType], 10))
    .reduce((acum, currValue) => acum + currValue, 0);
}

function ticketCount(departs, returns) {
  let tickets = departs.tickets.length;

  if (returns) {
    tickets += returns.tickets.length;
  }

  return tickets;
}

function serializeReservation(reservation, prefix, isOpenTicket) {
  const { fragments, selectedFare } = reservation;
  const transportType =
    fragments.length > 1 ? 'mixed' : fragments[0].transportType;
  const trip = transportType === 'mixed' ? reservation : fragments[0];
  const properties = {};
  const serializedTrip = serializeTrip(transportType, trip);

  // rename the "Departure" property since the the name changes
  // to "Departure|Return Time" in purchase related events
  serializedTrip.Time = serializedTrip.Departure;
  Reflect.deleteProperty(serializedTrip, 'Departure');
  // delete "Way" property since it useless for events with prefixes
  Reflect.deleteProperty(serializedTrip, 'Way');
  Object.keys(serializedTrip).forEach((tripKey) => {
    properties[`${prefix} ${tripKey}`] = serializedTrip[tripKey];
  });

  if (isOpenTicket) {
    properties[`${prefix} Time`] = null;
    properties[`${prefix} Arrival`] = null;
  }

  if (selectedFare) {
    properties[`${prefix} Selected Fare`] = selectedFare;
  }

  return properties;
}

function getTripLength(departs, returns) {
  if (!departs || !returns) return null;

  const departureDay = moment(departs.arrival).startOf('day');
  const returnDay = moment(returns.arrival).startOf('day');
  const tripLength = moment.duration(returnDay - departureDay).asDays();

  return tripLength;
}

export default function serializePurchase(purchase) {
  const {
    departs,
    isViajaAhorrando,
    openTicket,
    passengerSelection,
    paymentEngine,
    pricing: { total, markupPercentage, isExperimentalMarkup },
    returns,
    token,
    experimental,
    serviceLevel,
    lang = 'es-MX',
    isUsingAdjacentSeats,
    recommendedTripType,
    useRecommendedTrip,
    resultSeats,
    bulkTicket,
    departureDaysCarouselUsed,
    departureDaysCarouselDateSelected,
    departureSearchOriginalDate,
    departureDaysCarouselDateDeltaDays,
    returnDaysCarouselDateSelected,
    returnSearchOriginalDate,
    returnDaysCarouselDateDeltaDays,
    daysCarouselInteractionCount,
  } = purchase;
  const serializedDeparture = serializeReservation(
    departs,
    'Departure',
    openTicket
  );
  const serializedReturn =
    returns && serializeReservation(returns, 'Return', openTicket);
  const tickets = ticketCount(departs, returns);
  const purchaseProps = {
    'Experimental': experimental,
    'Service Level': serviceLevel,
    'Markup Experimental': isExperimentalMarkup,
    'Markup Percentage': markupPercentage,
    'Open Ticket': openTicket,
    'Passenger Count': passengerCount(passengerSelection),
    'Payment Engine': paymentEngine,
    'Routes': returns ? 2 : 1,
    'Tickets': tickets > 0 ? tickets : undefined,
    'Token': token,
    'Total': total,
    'Trip Length': openTicket ? null : getTripLength(departs, returns),
    'Language': lang,
    'Viaja Ahorrando': isViajaAhorrando,
    'Adjacent Seats': isUsingAdjacentSeats,
    'Recommended Trip Type': recommendedTripType,
    'Recommended Trip': useRecommendedTrip,
    'Result Seats': resultSeats,
    'Bulk Ticket': bulkTicket,
    'Departure Days Carousel Used': departureDaysCarouselUsed,
    'Departure Days Carousel Date Selected': departureDaysCarouselDateSelected,
    'Departure Search Original Date': departureSearchOriginalDate,
    'Departure Days Carousel Date Delta Days': departureDaysCarouselDateDeltaDays,
    'Return Days Carousel Date Selected': returnDaysCarouselDateSelected,
    'Return Search Original Date': returnSearchOriginalDate,
    'Return Days Carousel Date Delta Days': returnDaysCarouselDateDeltaDays,
    'Days Carousel Interaction Count': daysCarouselInteractionCount,
  };

  return Object.assign(purchaseProps, serializedDeparture, serializedReturn);
}



// WEBPACK FOOTER //
// ./serializers/purchase.js